import { motion } from 'motion/react';
import { memo, useState, useMemo, useRef, useCallback } from 'react';
import ChevronDown from '@/app/components/questionnaireV2/icons/chevron-down';
import {
  HealthDataPoint,
  QuestionnaireCategoryItemsItemOneOf as Question,
  QuestionnaireResponseQuestionResponse,
} from '@/api/generated-catch-backend/model';
import { EditIcon } from '@/components/icons/edit-icon';
import CategoryCardBase from './category-card-base';
import { QuestionnaireEditModal } from '@/app/components/questionnaireV2/components/questionnaire-edit-modal/questionnaire-edit-modal';
import { useGetQuestionDetailsV1HealthDataSlugQuestionDetailsGet } from '@/api/generated-catch-backend/health-data/health-data';
import { WarningIcon } from '@/app/components/questionnaireV2/icons/warning-icon';
import { ExpandedDataCategoryCardProps } from './types';

function isValidQuestionResponse(
  data: QuestionnaireResponseQuestionResponse | undefined,
): data is QuestionnaireResponseQuestionResponse & { question: Question } {
  return (
    !!data?.question && 'questionnaireItemKind' in data.question && data.question.questionnaireItemKind === 'question'
  );
}

function QuestionRow({
  dataPoint,
  onEdit,
  isLoading,
  ref,
}: {
  dataPoint: HealthDataPoint;
  onEdit: (slug: string) => void;
  isLoading: boolean;
  ref?: (element: HTMLDivElement | null) => void;
}) {
  return (
    <div ref={ref} className="flex items-start gap-4 py-3">
      {/* Warning Icon or Spacer */}
      {dataPoint.requiresAttention && (
        <div className="flex w-8 items-center">{dataPoint.requiresAttention && <WarningIcon />}</div>
      )}
      {/* Question Content */}
      <div className="flex flex-1 flex-col gap-1">
        <p className="text-xs leading-[18px] font-normal tracking-[0.2px] text-[#183344]">{dataPoint.displayName}</p>
        <p className="text-base leading-6 font-medium text-[#183344]">
          {dataPoint.value && dataPoint.unit
            ? `${dataPoint.value} ${dataPoint.unit}`
            : dataPoint.value || 'Not provided'}
        </p>
      </div>

      {/* Edit Icon / Loading Spinner */}
      <div className="flex items-center">
        <button
          onClick={() => onEdit(dataPoint.slug)}
          className="flex items-center justify-center rounded-lg p-2 transition-colors hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50"
          aria-label={`Edit ${dataPoint.displayName}`}
          type="button"
          disabled={isLoading}
        >
          {isLoading ? (
            <div className="h-5 w-5 animate-spin rounded-full border-2 border-gray-300 border-t-[#183344]" />
          ) : (
            <EditIcon />
          )}
        </button>
      </div>
    </div>
  );
}

function SectionHeader({ title }: { title: string }) {
  return (
    <div className="border-t border-[#d4c2b5] pt-6 pb-3">
      <h4 className="text-xl leading-[1.3] font-bold text-[#183344]">{title}</h4>
    </div>
  );
}

export const ExpandedDataCategoryCard = memo(function ExpandedDataCategoryCard({
  name,
  description,
  icon,
  progressValue,
  progressMax,
  lastUpdated,
  isComplete,
  subcategories,
  isExpanded,
  onToggleExpanded,
}: ExpandedDataCategoryCardProps) {
  const [editingSlug, setEditingSlug] = useState<string | null>(null);
  const dataPointRefs = useRef<Map<string, HTMLDivElement>>(new Map());
  const categoryCardRef = useRef<HTMLDivElement>(null);

  const editingDataPoint = useMemo(() => {
    if (!editingSlug) return null;
    return subcategories.flatMap((sub) => sub.dataPoints).find((dp) => dp.slug === editingSlug);
  }, [editingSlug, subcategories]);

  // Fetch question details when editingSlug is set (triggered by button click)
  const questionDetailsQuery = useGetQuestionDetailsV1HealthDataSlugQuestionDetailsGet(
    editingSlug || '',
    { 'question-slug': editingDataPoint?.sourceId || '' },
    {
      query: {
        enabled: !!editingSlug && !!editingDataPoint?.sourceId, // Fetch immediately when we have a slug
        retry: 1,
        staleTime: 5 * 60 * 1000, // Cache for 5 minutes
        gcTime: 10 * 60 * 1000, // Keep in cache for 10 minutes
      },
    },
  );

  const handleEditDataPoint = (slug: string) => {
    setEditingSlug(slug);
    // Modal will open automatically when data is loaded (see below)
  };

  const handleEditComplete = useCallback(() => {
    setEditingSlug(null);
  }, []);

  return (
    <div ref={categoryCardRef}>
      <CategoryCardBase
        name={name}
        description={description}
        icon={icon}
        progressValue={progressValue}
        progressMax={progressMax}
        lastUpdated={lastUpdated}
        isComplete={isComplete}
      >
        {/* Edit Button */}
        <motion.button
          onClick={onToggleExpanded}
          className="relative box-border flex h-[40px] w-full shrink-0 items-center justify-center rounded-[100px] bg-[#183344] px-[20px] lg:max-w-72"
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          transition={{ duration: 0.2 }}
          aria-expanded={isExpanded}
          aria-controls={`${name.toLowerCase().replace(/\s+/g, '-')}-content`}
          aria-label={`${isExpanded ? 'Collapse' : 'Expand'} ${name} details`}
          type="button"
        >
          <span className="text-[14px] leading-[1.4] font-bold text-white">Edit</span>
          <motion.div
            className="absolute right-5 h-4 w-4 text-white"
            animate={{ rotate: isExpanded ? 180 : 0 }}
            transition={{ duration: 0.2 }}
          >
            <ChevronDown className="h-full w-full" />
          </motion.div>
        </motion.button>

        {/* Accessibility Live Region */}
        <div aria-live="polite" aria-atomic="true" className="sr-only">
          {isExpanded ? `${name} expanded` : `${name} collapsed`}
        </div>

        {/* Expanded Content */}
        {isExpanded && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            transition={{
              duration: 0.3,
              ease: 'easeInOut',
            }}
            className="w-full overflow-hidden pt-[18px]"
            id={`${name.toLowerCase().replace(/\s+/g, '-')}-content`}
            role="region"
            aria-label={`${name} details`}
          >
            <div className="flex w-full flex-col">
              {subcategories.map((subcategory, subcategoryIndex) => (
                <div key={subcategory.id} className="flex w-full flex-col">
                  {/* Subcategory Header - skip if name is "-" */}
                  <SectionHeader title={subcategory.name === '-' ? '' : subcategory.name} />

                  {/* Data Points in Subcategory */}
                  <div className="flex flex-col">
                    {subcategory.dataPoints.map((dataPoint, dataPointIndex) => (
                      <div key={dataPoint.id}>
                        <QuestionRow
                          dataPoint={dataPoint}
                          onEdit={handleEditDataPoint}
                          isLoading={editingSlug === dataPoint.slug && questionDetailsQuery.isLoading}
                          ref={(element) => {
                            if (element) {
                              dataPointRefs.current.set(dataPoint.slug, element);
                            } else {
                              dataPointRefs.current.delete(dataPoint.slug);
                            }
                          }}
                        />
                        {/* Add bottom border except for last data point in last subcategory */}
                        {!(
                          subcategoryIndex === subcategories.length - 1 &&
                          dataPointIndex === subcategory.dataPoints.length - 1
                        ) && <div className="h-px w-full" />}
                      </div>
                    ))}
                  </div>

                  {/* Subcategory bottom border - skip if name is "-" or if last subcategory */}
                  {subcategory.name !== '-' && subcategoryIndex < subcategories.length - 1 && (
                    <div className="border-b border-[#d4c2b5] pb-6" />
                  )}
                </div>
              ))}
            </div>
          </motion.div>
        )}

        {/* Edit Modal - only show when we have data loaded */}
        {editingSlug &&
          editingDataPoint &&
          isValidQuestionResponse(questionDetailsQuery.data) &&
          !questionDetailsQuery.isFetching && (
            <QuestionnaireEditModal
              currentAnswer={questionDetailsQuery.data.currentAnswer ?? null}
              question={questionDetailsQuery.data.question}
              healthDataPointSlug={editingSlug}
              title="Edit"
              subtitle={editingDataPoint.displayName || editingSlug}
              onClose={handleEditComplete}
              isOpen={true}
            />
          )}

        {/* Error state */}
        {editingSlug && questionDetailsQuery.isError && (
          <div className="bg-opacity-50 fixed inset-0 z-50 flex items-center justify-center bg-black">
            <div className="rounded-lg bg-white p-6">
              <div className="text-center text-red-600">Failed to load health data point. Please try again later.</div>
              <button
                onClick={handleEditComplete}
                className="mt-4 rounded bg-gray-500 px-4 py-2 text-white hover:bg-gray-600"
                type="button"
              >
                Close
              </button>
            </div>
          </div>
        )}
      </CategoryCardBase>
    </div>
  );
});

export default ExpandedDataCategoryCard;
